import React, { useState, useEffect } from "react";
import { Layout, Select, Image, Alert, Modal, Input } from "antd";
import CardMonitor from "../../components/dashboard/CardMonitor";
import MobileDashboard from "../../components/mobile/MobileDashboard";
import {
    ExclamationOutlined,
    BookOutlined,
    CheckCircleOutlined,
    IssuesCloseOutlined
} from "@ant-design/icons";
import MapDemo from "../../components/dashboard/MapDemoo";

// ---------- Services ----------
import { getAllSitesGeoService } from "../../services/geolocation";
import { getPatrolBySiteUuidsService } from "../../services/dashboard";
import { getAllSitesCheckpointReportService } from "../../services/checkpoint";
import { getAllSitesLogs } from "../../services/daily";
import {
  getReportPatrolService,
  updateReportPatrolService,
  deleteReportService,
  getReportDailyService,
  toggleAcknowledgeReportPatrolService
} from "../../services/report";

// ---------- Date Libraries ----------
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const { Content: AntdContent } = Layout;
dayjs.extend(utc);
dayjs.extend(timezone);

const ContentComp = ({ colorBgContainer, borderRadiusLG }) => {
  // -------------------- State --------------------
  const [geo, setGeo] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const [mode, setMode] = useState("1");

  // รายงานการตรวจตรา (รายงาน Checkpoint)
  const [report, setReport] = useState([]);
  // รายงานผิดปกติ
  const [patrolAbnormal, setPatrolAbnormal] = useState([]);
  // Logs สำหรับรายงานประจำวัน
  const [logs, setLogs] = useState([]);
  // Logs สำหรับติดตามการส่งรายงานตรวจตรา
  const [logsReport, setLogsReport] = useState([]);
  // Logs สำหรับติดตามการส่งรายงานประจำวัน
  const [logsDaily, setLogsDaily] = useState([]);

  const [companyName, setCompanyName] = useState("");

  // Modal สำหรับแก้ไข "metadata" ของรายงานตรวจตรา
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentMetadata, setCurrentMetadata] = useState("");
  const [currentItem, setCurrentItem] = useState(null);

  // Modal สำหรับส่ง Email
  const [isSendModalVisible, setIsSendModalVisible] = useState(false);
  const [mail, setMail] = useState("");
  const [mailBackup, setMailBackup] = useState("");
  const [mailTitle, setMailTitle] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [includeMapImage, setIncludeMapImage] = useState(false);
  const [includeEmployeeImage, setIncludeEmployeeImage] = useState(false);

  // Modal ยืนยันการลบ
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Modal สรุปสำหรับการส่งรายงานประจำวัน
  const [isModalDailyVisible, setIsModalDailyVisible] = useState(false);
  const [selectedDailyMetadata, setSelectedDailyMetadata] = useState(null);

  // Modal สรุปสำหรับการส่งรายงานตรวจตรา
  const [isModalPatrolVisible, setIsModalPatrolVisible] = useState(false);
  const [selectedPatrolMetadata, setSelectedPatrolMetadata] = useState(null);

  // -------------------- Utility --------------------
  const metadataCounts = logsReport?.reduce((acc, item) => {
    const key = item.metadata;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const metadataCountsDaily = logsDaily?.reduce((acc, item) => {
    const key = item.metadata;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // -------------------- Functions --------------------
  const fetchAllData = async () => {
    // 1) ดึงข้อมูลแผนที่
    const geoResult = await getAllSitesGeoService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setGeo(geoResult.slice(0, 10));

    // 2) ดึง Logs ประจำวัน
    const logsResult = await getAllSitesLogs(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setLogs(logsResult.slice(0, 10));

    // 3) ดึงรายงาน Checkpoint ล่าสุด
    const reportResult = await getAllSitesCheckpointReportService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setReport(reportResult.slice(0, 10));

    // 4) ดึงรายงานส่งแจ้ง Patrol
    const logsReportResult = await getReportPatrolService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"],
      "after_report"
    );
    setLogsReport(logsReportResult);

    // 5) ดึงรายงานส่งแจ้ง Daily
    const logsDailyResult = await getReportDailyService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"],
      "check_report"
    );
    setLogsDaily(logsDailyResult);

    // 6) ดึงรายงานผิดปกติ
    const patrolAbnormalResult = await getPatrolBySiteUuidsService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"],
      "abnormal"
    );
    setPatrolAbnormal(patrolAbnormalResult);
  };

  // ดึงข้อมูลเมื่อ Mount
  useEffect(() => {
    setCompanyName(
      JSON.parse(localStorage.getItem("account"))?.company_name ||
      JSON.parse(localStorage.getItem("account"))?.site_name
    );

    fetchAllData();

    // ตั้ง Interval ให้ดึงข้อมูลทุก 15 วินาที (ตามโค้ดเดิม)
    const intervalId = setInterval(() => {
      fetchAllData();
    }, 15 * 1000);

    // Clear interval เมื่อ unmount
    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [localStorage.getItem("site")]);

  const handleToggleAcknowledgeReportPatrol = async (id) => {
    await toggleAcknowledgeReportPatrolService(id);
    // หลังอัปเดตสถานะ acknowledge แล้ว ให้ดึงข้อมูลใหม่
    const reportResult = await getAllSitesCheckpointReportService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setReport(reportResult.slice(0, 10));
  };

  // -------------------- แก้ไข Report Metadata --------------------
  const showModal = (item) => {
    setCurrentMetadata(item.metadata);
    setCurrentItem(item);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    if (!currentItem) return;
    await updateReportPatrolService(currentItem.id, currentMetadata);
    setIsModalVisible(false);

    // อัปเดตข้อมูลหลังแก้ไขรายงาน
    const reportResult = await getAllSitesCheckpointReportService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setReport(reportResult.slice(0, 10));
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // -------------------- ส่งรายงานทางอีเมล --------------------
  const showSendModal = (item) => {
    setCurrentItem(item);
    setMailBody(item.metadata);
    setIsSendModalVisible(true);
  };

  const handleSendReport = async () => {
    if (isSending) return;
    setIsSending(true);

    let bodyWithImages = mailBody;
    // ถ้าผู้ใช้เลือกให้แนบรูปแผนที่
    if (includeMapImage && currentItem?.map_image) {
      bodyWithImages += `<br><br>แผนที่การตรวจตรา<br><img src="https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${currentItem.map_image}" alt="Map Image" style="max-width: 100%; height: auto;">`;
    }
    // ถ้าผู้ใช้เลือกให้แนบรูปพนักงาน
    if (includeEmployeeImage && currentItem?.employee_image) {
      bodyWithImages += `<br><br>ยืนยันตัวตน<br><img src="https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${currentItem.employee_image}" alt="Employee Image" style="max-width: 100%; height: auto;">`;
    }

    try {
      const response = await fetch('https://send.siamai.tech/v1/mail/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mail,
          mail_backup: mailBackup,
          mail_title: mailTitle,
          mail_body: bodyWithImages,
        }),
      });

      if (response.ok) {
        alert('ส่งรายงานสำเร็จ');
        setIsSendModalVisible(false);
      } else {
        alert('เกิดข้อผิดพลาดในการส่งรายงาน');
      }
    } catch (error) {
      console.error('Error sending report:', error);
      alert('เกิดข้อผิดพลาดในการส่งรายงาน');
    } finally {
      setIsSending(false);
    }
  };

  // -------------------- ลบ Report --------------------
  const showDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    await deleteReportService(itemToDelete.id);
    setIsDeleteModalVisible(false);
    alert('ลบข้อมูลสำเร็จ');

    // อัปเดตข้อมูลหลังการลบ
    const reportResult = await getAllSitesCheckpointReportService(
      JSON.parse(localStorage.getItem("account"))["account_uuid"]
    );
    setReport(reportResult.slice(0, 10));
  };

  // -------------------- Modal สรุป Log (Daily/Patrol) --------------------
  const showDailyModal = (key) => {
    setSelectedDailyMetadata(key);
    setIsModalDailyVisible(true);
  };
  const handleCloseDailyModal = () => {
    setIsModalDailyVisible(false);
  };

  const showPatrolModal = (key) => {
    setSelectedPatrolMetadata(key);
    setIsModalPatrolVisible(true);
  };
  const handleClosePatrolModal = () => {
    setIsModalPatrolVisible(false);
  };

  // ฟังก์ชัน Helper สำหรับแสดงข้อความสถานะ
  const getDisplayName = (metadata, type) => {
    if (type === "daily") {
      switch (metadata) {
        case "null":
          return "ยังไม่ถึงรอบส่งรายงานประจำวัน";
        case "SUCCESS":
          return "ส่งสำเร็จ";
        case "REPORT NOT FOUND":
          return "ไม่พบข้อมูลรายงาน";
        case "ERROR":
          return "พบปัญหาในระบบ";
        default:
          return metadata;
      }
    } else if (type === "patrol") {
      switch (metadata) {
        case "null":
          return "ยังไม่ถึงรอบส่งรายงานตรวจตรา";
        case "SUCCESS":
          return "ส่งสำเร็จ";
        case "PATROL NOT FOUND":
          return "ไม่พบข้อมูลตรวจตรา";
        case "ERROR":
          return "พบปัญหาในระบบ";
        default:
          return metadata;
      }
    }
  };

  // -------------------- Render --------------------
  return (
    <AntdContent
      style={{
        margin: isMobile ? "0" : "24px 16px",
        padding: isMobile ? 0 : 24,
        minHeight: 360,
        background: isMobile ? "transparent" : colorBgContainer,
        borderRadius: isMobile ? 0 : borderRadiusLG,
      }}
    >
      {isMobile ? (
        <MobileDashboard 
          data={{
            today: logs?.length || 0,
            security: report?.length || 0,
            avgTime: "45 นาที",
            users: geo?.length || 0
          }}
          title="ภาพรวมระบบรักษาความปลอดภัย"
        />
      ) : (
        <>
          {/* 
            ✅ ใช้ Tailwind ในการจัด Grid เพื่อให้ Layout
            รองรับทั้งมือถือ (mobile) และ Desktop
          */}
          <div className="mt-2 md:mt-4 grid grid-cols-1 lg:grid-cols-3 gap-2 md:gap-4">
        {/* ====================== Card รายงานประจำวัน ====================== */}
        <CardMonitor
          color="green"
          icon={<CheckCircleOutlined />}
          title={
            <div className="flex justify-between">
              <div>รายงานประจำวัน</div>
            </div>
          }
          actions={
            <div className="overflow-y-auto max-h-[410px] space-y-2 text-left">
              {logs?.map((item, index) => (
                <div key={index}>
                  <Alert
                    className="w-full"
                    message={<div className="ml-4">{item.site_name}</div>}
                    description={
                      <>
                        <div className="ml-4">
                          <p className="font-bold mb-0">บันทึกข้อความ:</p>
                          {item.meta.split("\n").map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </div>
                      </>
                    }
                    type="success"
                    icon={
                      <div className="flex flex-col space-y-4">
                        <Image
                          width={100}
                          src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${item.image}`}
                        />
                      </div>
                    }
                    showIcon
                    closable
                  />
                </div>
              ))}
            </div>
          }
        />

        {/* ====================== Card รายงานการตรวจตรา ====================== */}
        <CardMonitor
          color="black"
          icon={<BookOutlined />}
          title={
            <div className="flex justify-between">
              <div>รายงานการตรวจตรา</div>
            </div>
          }
          actions={
            <div className="overflow-y-auto max-h-[410px] space-y-2 text-left">
              {report?.map((item, index) => (
                <div key={index}>
                  <Alert
                    className="w-full"
                    message={<div className="ml-4">{item.site_name}</div>}
                    description={
                      <div className="ml-4">
                        <p className="font-bold mb-0">บันทึกข้อความ:</p>
                        {item.metadata.split("\n").map((ln, idx) => (
                          <div key={idx}>{ln}</div>
                        ))}
                      </div>
                    }
                    type={item.acknowledge ? "success" : "error"}
                    icon={
                      <div className="flex flex-col space-y-4">
                        <Image
                          width={100}
                          src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${item.map_image}`}
                        />
                        {item.employee_image && (
                          <Image
                            width={100}
                            src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${item.employee_image}`}
                          />
                        )}
                        <button
                          onClick={() => handleToggleAcknowledgeReportPatrol(item.id)}
                          className={`${
                            item.acknowledge ? "bg-green-500" : "bg-gray-500"
                          } text-white px-4 py-2 rounded-md`}
                        >
                          {item.acknowledge ? "รับทราบแล้ว" : "รับทราบ"}
                        </button>
                        <button
                          onClick={() => showModal(item)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-md"
                        >
                          แก้ไข
                        </button>
                        <button
                          onClick={() => showDeleteModal(item)}
                          className="bg-red-500 text-white px-4 py-2 rounded-md"
                        >
                          ลบ
                        </button>
                        <button
                          className="text-black px-4 py-2 rounded-md border border-gray-300"
                          onClick={() => showSendModal(item)}
                        >
                          ส่งรายงาน
                        </button>
                      </div>
                    }
                    showIcon
                    closable
                  />
                </div>
              ))}
            </div>
          }
        />

        {/* ====================== Card รายงานเหตุการณ์ผิดปกติ ====================== */}
        <CardMonitor
          color="#FF0000"
          icon={<ExclamationOutlined />}
          title={
            <div className="flex justify-between">
              <div>รายงานเหตุการณ์ผิดปกติ</div>
            </div>
          }
          actions={
            <div className="overflow-y-auto max-h-[410px] space-y-2 text-left">
              {patrolAbnormal?.map((item, index) => (
                <div key={index}>
                  <Alert
                    className="w-full"
                    message={<div className="ml-4">{item.site_name}</div>}
                    description={
                      <>
                        <div className="ml-4">
                          <p className="font-bold mb-0">{item.report_name}</p>
                          <p className="font-bold mb-0">บันทึกข้อความ:</p>
                          {item.report_message.includes("\n")
                            ? item.report_message
                                .split("\n")
                                .slice(1, -1)
                                .map((txt, idx) => <div key={idx}>{txt}</div>)
                            : item.report_message}
                          <p className="font-bold mb-0">
                            วันที่:{" "}
                            {moment(
                              item.created_at.replace("+00:00", "")
                            ).format("DD/MM/YYYY HH:mm")}
                          </p>
                        </div>
                      </>
                    }
                    type="error"
                    icon={
                      <Image
                        width={100}
                        src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/${item.report_img[0]}`}
                      />
                    }
                    showIcon
                    closable
                  />
                </div>
              ))}
            </div>
          }
        />
      </div>

      {/* ====================== ติดตามการส่งรายงานประจำวัน (Daily) ====================== */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardMonitor
          color="green"
          icon={<IssuesCloseOutlined />}
          title={<div className="flex justify-between">ติดตามการส่งรายงานประจำวัน</div>}
          actions={
            <div className="overflow-y-auto max-h-[410px] space-y-4 p-4 bg-gray-50 rounded-lg shadow-md">
              {/* สรุปประเภท metadata */}
              <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border">
                <h3 className="font-semibold text-lg">
                  📊 สรุปสถานะการส่งรายงานประจำวัน
                </h3>
                <ul className="mt-2 text-sm space-y-1">
                  {Object.entries(metadataCountsDaily).map(([key, count]) => (
                    <li
                      key={key}
                      className="flex justify-between cursor-pointer hover:bg-gray-200 p-2 rounded"
                      onClick={() => showDailyModal(key)}
                    >
                      <span>
                        {key === "SUCCESS"
                          ? "✅ ส่งสำเร็จ"
                          : key === "REPORT NOT FOUND"
                          ? "⚠️ ไม่พบข้อมูลรายงาน"
                          : key === "ERROR"
                          ? "❌ พบปัญหาในระบบ"
                          : `🔍 ${
                              key === "null"
                                ? "ยังไม่ถึงรอบส่งรายงานประจำวัน"
                                : key
                            }`}
                      </span>
                      <span className="font-semibold">{count} รายการ</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* รายการ Alert */}
              {logsDaily?.map((item, index) => (
                <Alert
                  key={index}
                  className={`p-4 rounded-lg shadow-sm w-full ${
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "bg-gray-100 border border-gray-600"
                        : item.metadata === "REPORT NOT FOUND"
                        ? "bg-red-100 border border-red-500"
                        : "bg-orange-100 border border-orange-500"
                      : "bg-green-100 border border-green-500"
                  }`}
                  message={
                    <>
                      <h3 className="font-semibold text-lg">{item.site_name}</h3>
                      <p
                        className={`font-semibold ${
                          item.metadata !== "SUCCESS"
                            ? item.is_send === false
                              ? "text-gray-600"
                              : item.metadata === "REPORT NOT FOUND"
                              ? "text-red-600"
                              : "text-orange-600"
                            : "text-green-600"
                        }`}
                      >
                        {item.metadata !== "SUCCESS"
                          ? item.metadata === "REPORT NOT FOUND" && item.is_send === true
                            ? "ไม่พบข้อมูลการส่งรายงาน"
                            : item.is_send === false
                            ? "ยังไม่ถึงรอบส่งรายงาน"
                            : "พบปัญหาจากระบบ"
                          : "ส่งรายงานเรียบร้อย"}
                      </p>
                    </>
                  }
                  description={
                    <div className="mt-2 text-sm">
                      <p className="font-bold">รอบเวลาส่งรายงาน:</p>
                      <p className="ml-2">เวลา {item.report_time.replace(":00", "")}</p>
                      <p className="font-bold mt-2">เวลาส่งรายงาน:</p>
                      <p className="ml-2">
                        {dayjs
                          .utc(item.send_time)
                          .tz("Asia/Bangkok")
                          .format("DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  }
                  type={
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "info"
                        : item.metadata === "REPORT NOT FOUND"
                        ? "warning"
                        : "error"
                      : "success"
                  }
                  showIcon
                />
              ))}
            </div>
          }
        />

        {/* Modal แสดงรายการในหมวด Daily */}
        <Modal
          title={`📋 รายการ ${getDisplayName(selectedDailyMetadata, "daily")}`}
          open={isModalDailyVisible}
          onCancel={handleCloseDailyModal}
          footer={null}
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }} // เพิ่มเพื่อเลื่อน Modal ได้บน Mobile
        >
          <div className="space-y-4">
            {logsDaily
              ?.filter((item) =>
                JSON.stringify(item.metadata) === "null"
                  ? JSON.stringify(item.metadata) === selectedDailyMetadata
                  : item.metadata === selectedDailyMetadata
              )
              .map((item, index) => (
                <Alert
                  key={index}
                  className={`p-4 rounded-lg shadow-sm ${
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "bg-gray-100 border border-gray-600"
                        : item.metadata === "REPORT NOT FOUND"
                        ? "bg-red-100 border border-red-500"
                        : "bg-orange-100 border border-orange-500"
                      : "bg-green-100 border border-green-500"
                  }`}
                  message={
                    <>
                      <h3 className="font-semibold text-lg">{item.site_name}</h3>
                      <p className="text-sm">
                        รอบเวลาส่งรายงานประจำวัน: {item.report_time.replace(":00", "")}
                      </p>
                      <p className="text-sm">
                        เวลาส่งรายงานประจำวัน:{" "}
                        {dayjs
                          .utc(item.send_time)
                          .tz("Asia/Bangkok")
                          .format("DD/MM/YYYY HH:mm")}
                      </p>
                    </>
                  }
                  type={
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "info"
                        : item.metadata === "REPORT NOT FOUND"
                        ? "warning"
                        : "error"
                      : "success"
                  }
                  showIcon
                />
              ))}
          </div>
        </Modal>

        {/* ====================== Card ติดตามการส่งรายงานตรวจตรา (Patrol) ====================== */}
        <CardMonitor
          color="green"
          icon={<IssuesCloseOutlined />}
          title={<div className="flex justify-between">ติดตามการส่งรายงานตรวจตรา</div>}
          actions={
            <div className="overflow-y-auto max-h-[410px] space-y-4 p-4 bg-gray-50 rounded-lg shadow-md">
              {/* สรุปประเภท metadata */}
              <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border">
                <h3 className="font-semibold text-lg">
                  📊 สรุปสถานะการส่งรายงานตรวจตรา
                </h3>
                <ul className="mt-2 text-sm space-y-1">
                  {Object.entries(metadataCounts).map(([key, count]) => (
                    <li
                      key={key}
                      className="flex justify-between cursor-pointer hover:bg-gray-200 p-2 rounded"
                      onClick={() => showPatrolModal(key)}
                    >
                      <span>
                        {key === "SUCCESS"
                          ? "✅ ส่งสำเร็จ"
                          : key === "PATROL NOT FOUND"
                          ? "⚠️ ไม่พบข้อมูลตรวจตรา"
                          : key === "ERROR"
                          ? "❌ พบปัญหาในระบบ"
                          : `🔍 ${
                              key === "null"
                                ? "ยังไม่ถึงรอบส่งรายงานตรวจตรา"
                                : key
                            }`}
                      </span>
                      <span className="font-semibold">{count} รายการ</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* รายการ Alert */}
              {logsReport?.map((item, index) => (
                <Alert
                  key={index}
                  className={`p-4 rounded-lg shadow-sm w-full ${
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "bg-gray-100 border border-gray-600"
                        : item.metadata === "PATROL NOT FOUND"
                        ? "bg-red-100 border border-red-500"
                        : "bg-orange-100 border border-orange-500"
                      : "bg-green-100 border border-green-500"
                  }`}
                  message={
                    <>
                      <h3 className="font-semibold text-lg">{item.site_name}</h3>
                      <p
                        className={`font-semibold ${
                          item.metadata !== "SUCCESS"
                            ? item.is_send === false
                              ? "text-gray-600"
                              : item.metadata === "PATROL NOT FOUND"
                              ? "text-red-600"
                              : "text-orange-600"
                            : "text-green-600"
                        }`}
                      >
                        {item.metadata !== "SUCCESS"
                          ? item.metadata === "PATROL NOT FOUND" && item.is_send === true
                            ? "ไม่พบข้อมูลการตรวจตรา"
                            : item.is_send === false
                            ? "ยังไม่ถึงรอบส่งรายงานตรวจตรา"
                            : "พบปัญหาจากระบบ"
                          : "ส่งรายงานตรวจตราเรียบร้อย"}
                      </p>
                    </>
                  }
                  description={
                    <div className="mt-2 text-sm">
                      <p className="font-bold">รอบเวลาตรวจตรา:</p>
                      <p className="ml-2">
                        ตั้งแต่เวลา {item.start_time.replace(":00", "")} ถึง{" "}
                        {item.end_time.replace(":00", "")}
                      </p>
                      <p className="font-bold mt-2">เวลาส่งรายงานตรวจตรา:</p>
                      <p className="ml-2">
                        {dayjs
                          .utc(item.send_time)
                          .tz("Asia/Bangkok")
                          .format("DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  }
                  type={
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "info"
                        : item.metadata === "PATROL NOT FOUND"
                        ? "warning"
                        : "error"
                      : "success"
                  }
                  showIcon
                />
              ))}
            </div>
          }
        />

        {/* Modal แสดงรายการในหมวด Patrol */}
        <Modal
          title={`📋 รายการ ${getDisplayName(selectedPatrolMetadata, "patrol")}`}
          open={isModalPatrolVisible}
          onCancel={handleClosePatrolModal}
          footer={null}
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }} // เพิ่มเพื่อเลื่อน Modal ได้บน Mobile
        >
          <div className="space-y-4">
            {logsReport
              ?.filter((item) =>
                JSON.stringify(item.metadata) === "null"
                  ? JSON.stringify(item.metadata) === selectedPatrolMetadata
                  : item.metadata === selectedPatrolMetadata
              )
              .map((item, index) => (
                <Alert
                  key={index}
                  className={`p-4 rounded-lg shadow-sm ${
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "bg-gray-100 border border-gray-600"
                        : item.metadata === "PATROL NOT FOUND"
                        ? "bg-red-100 border border-red-500"
                        : "bg-orange-100 border border-orange-500"
                      : "bg-green-100 border border-green-500"
                  }`}
                  message={
                    <>
                      <h3 className="font-semibold text-lg">{item.site_name}</h3>
                      <p className="text-sm">
                        รอบเวลาตรวจตรา: {item.start_time.replace(":00", "")} -{" "}
                        {item.end_time.replace(":00", "")}
                      </p>
                      <p className="text-sm">
                        เวลาส่งรายงานตรวจตรา:{" "}
                        {dayjs
                          .utc(item.send_time)
                          .tz("Asia/Bangkok")
                          .format("DD/MM/YYYY HH:mm")}
                      </p>
                    </>
                  }
                  type={
                    item.metadata !== "SUCCESS"
                      ? item.is_send === false
                        ? "info"
                        : item.metadata === "PATROL NOT FOUND"
                        ? "warning"
                        : "error"
                      : "success"
                  }
                  showIcon
                />
              ))}
          </div>
        </Modal>
      </div>

      {/* ====================== แผนที่การตรวจตรา (Row เต็ม) ====================== */}
      <div className="mt-4">
        <CardMonitor
          className="w-full"
          title={
            <div className="flex flex-col lg:flex-row lg:justify-between gap-2">
              <div>แผนที่การตรวจตรา</div>
              <Select
                className="lg:ml-4 w-full lg:w-52"
                showSearch
                placeholder="ตำแหน่งล่าสุด"
                onChange={(value) => setMode(value)}
                value={mode}
                options={[{ value: "1", label: "ตำแหน่งล่าสุด" }]}
              />
            </div>
          }
          actions={
            <div className="w-full">
              <MapDemo mode={mode} now={geo} />
            </div>
          }
        />
      </div>

      {/* ====================== Modal แก้ไขรายงาน ====================== */}
      <Modal
        title="แก้ไขข้อมูลรายงาน"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <Input.TextArea
          value={currentMetadata}
          onChange={(e) => setCurrentMetadata(e.target.value)}
          rows={20}
        />
      </Modal>

      {/* ====================== Modal ส่งรายงานทางอีเมล ====================== */}
      <Modal
        title="📄 ส่งรายงาน"
        open={isSendModalVisible}
        onOk={handleSendReport}
        onCancel={() => setIsSendModalVisible(false)}
        okText="🚀 ส่ง"
        cancelText="❌ ยกเลิก"
        confirmLoading={isSending}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className="space-y-4 p-4">
          {/* Email Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              📧 อีเมลผู้รับรายงานหลัก
            </label>
            <Input
              className="border border-gray-300 rounded-lg px-4 py-2 w-full shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              placeholder="กรอกอีเมลผู้รับ..."
              value={mail}
              onChange={(e) => setMail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              📧 อีเมลผู้รับรายงานสำรอง
            </label>
            <Input
              className="border border-gray-300 rounded-lg px-4 py-2 w-full shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              placeholder="กรอกอีเมลผู้รับ..."
              value={mailBackup}
              onChange={(e) => setMailBackup(e.target.value)}
            />
          </div>

          {/* Title Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              📝 หัวข้อรายงาน
            </label>
            <Input
              className="border border-gray-300 rounded-lg px-4 py-2 w-full shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              placeholder="ระบุหัวข้อ..."
              value={mailTitle}
              onChange={(e) => setMailTitle(e.target.value)}
            />
          </div>

          {/* Image Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              🖼️ เลือกรูปภาพที่จะส่ง
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeMapImage}
                  onChange={(e) => setIncludeMapImage(e.target.checked)}
                />
                <span>แผนที่</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeEmployeeImage}
                  onChange={(e) => setIncludeEmployeeImage(e.target.checked)}
                />
                <span>รูปพนักงาน</span>
              </label>
            </div>
          </div>

          {/* Report Preview */}
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 shadow-inner">
            <p className="text-sm font-bold text-gray-600">📑 ตัวอย่างรายงาน:</p>
            <pre className="text-gray-700 text-sm bg-white p-2 rounded-md shadow-sm overflow-auto">
              {mailBody}
              {includeMapImage && currentItem?.map_image && (
                <div>
                  <img
                    src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${currentItem.map_image}`}
                    alt="Map"
                    style={{ maxWidth: "100%", marginTop: "10px" }}
                  />
                </div>
              )}
              {includeEmployeeImage && currentItem?.employee_image && (
                <div>
                  <img
                    src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${currentItem.employee_image}`}
                    alt="Employee"
                    style={{ maxWidth: "100%", marginTop: "10px" }}
                  />
                </div>
              )}
            </pre>
          </div>
        </div>
      </Modal>
        </>
      )}

      {/* ====================== Modal ยืนยันการลบ ====================== */}
      <Modal
        title="ยืนยันการลบ"
        open={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <p>คุณแน่ใจหรือว่าต้องการลบข้อมูลนี้?</p>
      </Modal>
    </AntdContent>
  );
};

export default ContentComp;